import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import Avatar from '../components/ui/Avatar';
import { useAuth } from '../hooks/useAuth';
import { clearUserCache } from '../hooks/useUserData';
import { isAdminUser } from '../constants/admin';
import { addDoc, collection, deleteDoc, doc, getDocs, limit, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { adminSendEmailBlast, adminSendTestEmail, bootstrapAdmin, listenEmailProgress } from '../services/adminEmailService';
import { uploadImage } from '../services/imageService';
import { updateAuthorInfoInUserPosts } from '../services/postService';

const AdminDashboardPage = () => {
  const { currentUser, userData } = useAuth();
  const isAdmin = useMemo(() => isAdminUser(currentUser), [currentUser]);
  const [email, setEmail] = useState(currentUser?.email || '');

  useEffect(() => {
    setEmail(currentUser?.email || '');
  }, [currentUser?.email]);

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('inicio'); // inicio | emails | plantillas | cuenta | usuarios
  const [premiumBusy, setPremiumBusy] = useState(false);
  const [premiumMessage, setPremiumMessage] = useState(null);

  // Usuarios (admin search)
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userActionBusy, setUserActionBusy] = useState(null); // uid when busy

  // Templates
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [editing, setEditing] = useState(null);
  const [templateBusy, setTemplateBusy] = useState(false);

  // Email test
  const [testTo, setTestTo] = useState(email || '');
  const [testSubject, setTestSubject] = useState('Test - Illustra');
  const [testMessage, setTestMessage] = useState('Hola! Este es un email de prueba enviado desde el panel de administración.');

  // Blast
  const [blastSubject, setBlastSubject] = useState('Illustra - Novedades');
  const [maxRecipients, setMaxRecipients] = useState(10);
  const [filterVerified, setFilterVerified] = useState(true);
  const [filterPremium, setFilterPremium] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');
  const [progressId, setProgressId] = useState('');
  const [progress, setProgress] = useState(null);
  const unsubProgressRef = useRef(null);

  useEffect(() => {
    // Load templates once
    let cancelled = false;
    setTemplatesLoading(true);
    (async () => {
      try {
        const q = query(collection(db, 'emailTemplates'), orderBy('updatedAt', 'desc'));
        const snap = await getDocs(q);
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTemplates(list);
        setSelectedTemplateId((prev) => prev || list[0]?.id || '');
      } catch (e) {
        if (!cancelled) setMessage({ type: 'error', text: e.message || 'No se pudieron cargar plantillas' });
      } finally {
        if (!cancelled) setTemplatesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (unsubProgressRef.current) unsubProgressRef.current();
    unsubProgressRef.current = null;
    if (!progressId) return;
    unsubProgressRef.current = listenEmailProgress(progressId, setProgress);
    return () => {
      if (unsubProgressRef.current) unsubProgressRef.current();
      unsubProgressRef.current = null;
    };
  }, [progressId]);

  useEffect(() => {
    if (activeTab !== 'usuarios' || !isAdmin) return;
    let cancelled = false;
    setUsersLoading(true);
    (async () => {
      try {
        const q = query(collection(db, 'users'), limit(200));
        const snap = await getDocs(q);
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUsersList(list);
      } catch (e) {
        if (!cancelled) setMessage({ type: 'error', text: e.message || 'No se pudieron cargar usuarios' });
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, isAdmin]);

  const filteredUsers = useMemo(() => {
    const q = (userSearchQuery || '').trim().toLowerCase();
    if (!q) return usersList;
    return usersList.filter((u) => {
      const name = (u.displayName || u.username || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      const uid = (u.id || u.uid || '').toLowerCase();
      return name.includes(q) || email.includes(q) || uid.includes(q);
    });
  }, [usersList, userSearchQuery]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const defaultTemplateDraft = useMemo(
    () => ({
      name: 'Nueva plantilla',
      slogan: 'Inspira. Crea. Comparte.',
      title: 'Hola desde Illustra',
      body: 'Escribí aquí tu mensaje.',
      ctaText: 'Visitar Illustra',
      ctaUrl: 'https://illustra-6ca8a.web.app',
      social: {
        twitter: '',
        instagram: '',
        github: ''
      },
      logoUrl: '',
      images: [],
      headerGradient: '#8B5CF6 0%, #7C3AED 100%'
    }),
    []
  );

  const startCreateTemplate = () => {
    setEditing({ ...defaultTemplateDraft });
    setActiveTab('plantillas');
  };

  const startEditTemplate = (t) => {
    setEditing({
      id: t.id,
      name: t.name || '',
      slogan: t.slogan || '',
      title: t.title || '',
      body: t.body || '',
      ctaText: t.ctaText || '',
      ctaUrl: t.ctaUrl || '',
      headerGradient: t.headerGradient || '#8B5CF6 0%, #7C3AED 100%',
      logoUrl: t.logoUrl || '',
      images: Array.isArray(t.images) ? t.images : [],
      social: {
        twitter: t.social?.twitter || '',
        instagram: t.social?.instagram || '',
        github: t.social?.github || ''
      }
    });
    setActiveTab('plantillas');
  };

  const saveTemplate = async () => {
    if (!editing) return;
    const name = (editing.name || '').trim();
    if (!name) {
      setMessage({ type: 'error', text: 'La plantilla debe tener un nombre' });
      return;
    }
    setTemplateBusy(true);
    setMessage(null);
    try {
      const payload = {
        ownerId: currentUser.uid,
        name,
        slogan: (editing.slogan || '').trim(),
        title: (editing.title || '').trim(),
        body: (editing.body || '').trim(),
        ctaText: (editing.ctaText || '').trim(),
        ctaUrl: (editing.ctaUrl || '').trim(),
        headerGradient: (editing.headerGradient || '').trim(),
        logoUrl: (editing.logoUrl || '').trim(),
        images: Array.isArray(editing.images) ? editing.images.slice(0, 3) : [],
        social: editing.social || {}
      };

      if (editing.id) {
        await updateDoc(doc(db, 'emailTemplates', editing.id), { ...payload, updatedAt: serverTimestamp() });
      } else {
        const ref = await addDoc(collection(db, 'emailTemplates'), {
          ...payload,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        setSelectedTemplateId(ref.id);
      }

      // refresh list
      const q = query(collection(db, 'emailTemplates'), orderBy('updatedAt', 'desc'));
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setTemplates(list);
      setMessage({ type: 'success', text: 'Plantilla guardada' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'No se pudo guardar la plantilla' });
    } finally {
      setTemplateBusy(false);
    }
  };

  const removeTemplate = async (id) => {
    if (!id) return;
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    setTemplateBusy(true);
    setMessage(null);
    try {
      await deleteDoc(doc(db, 'emailTemplates', id));
      const next = templates.filter((t) => t.id !== id);
      setTemplates(next);
      setSelectedTemplateId(next[0]?.id || '');
      if (editing?.id === id) setEditing(null);
      setMessage({ type: 'success', text: 'Plantilla eliminada' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'No se pudo eliminar la plantilla' });
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleGrantPremium = async () => {
    if (!currentUser?.uid) return;
    setPremiumBusy(true);
    setPremiumMessage(null);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        isPremium: true,
        premiumSince: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      clearUserCache(currentUser.uid);
      setPremiumMessage({ type: 'success', text: 'Premium activado para tu usuario.' });
    } catch (e) {
      setPremiumMessage({ type: 'error', text: e.message || 'No se pudo activar Premium.' });
    } finally {
      setPremiumBusy(false);
    }
  };

  const handleRevokePremium = async () => {
    if (!currentUser?.uid) return;
    setPremiumBusy(true);
    setPremiumMessage(null);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        isPremium: false,
        premiumSince: null,
        photoURL: null,
        profileImage: null,
        bannerURL: null,
        photoURLIsGif: false,
        bannerURLIsGif: false,
        recentAvatarURLs: [],
        recentBannerURLs: [],
        updatedAt: serverTimestamp()
      });
      await updateAuthorInfoInUserPosts(currentUser.uid, {
        profileImage: null,
        isPremium: false,
        premiumNameColor: null,
        photoURLIsGif: false
      });
      clearUserCache(currentUser.uid);
      setPremiumMessage({ type: 'success', text: 'Premium desactivado.' });
    } catch (e) {
      setPremiumMessage({ type: 'error', text: e.message || 'No se pudo quitar Premium.' });
    } finally {
      setPremiumBusy(false);
    }
  };

  const handleGrantPremiumForUser = async (uid) => {
    if (!uid) return;
    setUserActionBusy(uid);
    setMessage(null);
    try {
      await updateDoc(doc(db, 'users', uid), {
        isPremium: true,
        premiumSince: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      clearUserCache(uid);
      setUsersList((prev) => prev.map((u) => (u.id === uid ? { ...u, isPremium: true } : u)));
      setMessage({ type: 'success', text: 'Premium activado para el usuario.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'No se pudo activar Premium.' });
    } finally {
      setUserActionBusy(null);
    }
  };

  const handleRevokePremiumForUser = async (uid) => {
    if (!uid) return;
    setUserActionBusy(uid);
    setMessage(null);
    try {
      await updateDoc(doc(db, 'users', uid), {
        isPremium: false,
        premiumSince: null,
        updatedAt: serverTimestamp()
      });
      await updateAuthorInfoInUserPosts(uid, {
        profileImage: null,
        isPremium: false,
        premiumNameColor: null,
        photoURLIsGif: false
      });
      clearUserCache(uid);
      setUsersList((prev) => prev.map((u) => (u.id === uid ? { ...u, isPremium: false } : u)));
      setMessage({ type: 'success', text: 'Premium desactivado para el usuario.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'No se pudo quitar Premium.' });
    } finally {
      setUserActionBusy(null);
    }
  };

  const handleGrantAmbassadorForUser = async (uid) => {
    if (!uid) return;
    setUserActionBusy(uid);
    setMessage(null);
    try {
      await updateDoc(doc(db, 'users', uid), {
        isAmbassador: true,
        updatedAt: serverTimestamp()
      });
      clearUserCache(uid);
      setUsersList((prev) => prev.map((u) => (u.id === uid ? { ...u, isAmbassador: true } : u)));
      setMessage({ type: 'success', text: 'Embajador asignado.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'No se pudo asignar embajador.' });
    } finally {
      setUserActionBusy(null);
    }
  };

  const handleRevokeAmbassadorForUser = async (uid) => {
    if (!uid) return;
    setUserActionBusy(uid);
    setMessage(null);
    try {
      await updateDoc(doc(db, 'users', uid), {
        isAmbassador: false,
        updatedAt: serverTimestamp()
      });
      clearUserCache(uid);
      setUsersList((prev) => prev.map((u) => (u.id === uid ? { ...u, isAmbassador: false } : u)));
      setMessage({ type: 'success', text: 'Embajador quitado.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'No se pudo quitar embajador.' });
    } finally {
      setUserActionBusy(null);
    }
  };

  const uploadTemplateImage = async (file, kind) => {
    if (!file || !currentUser?.uid) return;
    setTemplateBusy(true);
    setMessage(null);
    try {
      const up = await uploadImage(file, `email-assets/${currentUser.uid}`, currentUser.uid);
      if (!up.success) throw new Error(up.error || 'No se pudo subir la imagen');
      setEditing((prev) => {
        if (!prev) return prev;
        if (kind === 'logo') return { ...prev, logoUrl: up.url };
        const imgs = Array.isArray(prev.images) ? prev.images.slice() : [];
        if (imgs.length >= 3) return prev;
        imgs.push({ url: up.url, alt: '' });
        return { ...prev, images: imgs };
      });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Error subiendo imagen' });
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleBootstrapAdmin = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await bootstrapAdmin();
      setMessage({ type: 'success', text: res?.data?.message || 'Admin configurado. Volvé a iniciar sesión para refrescar claims.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'No se pudo configurar admin' });
    } finally {
      setBusy(false);
    }
  };

  const handleSendTest = async () => {
    if (!selectedTemplateId) {
      setMessage({ type: 'error', text: 'Selecciona una plantilla' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const res = await adminSendTestEmail({
        to: (testTo || '').trim(),
        subject: (testSubject || '').trim(),
        message: (testMessage || '').trim(),
        templateId: selectedTemplateId
      });
      setMessage({ type: 'success', text: `Email enviado. ID: ${res?.data?.messageId || res?.data?.id || 'ok'}` });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'No se pudo enviar el email' });
    } finally {
      setBusy(false);
    }
  };

  const handleBlast = async () => {
    if (!selectedTemplateId) {
      setMessage({ type: 'error', text: 'Selecciona una plantilla' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const recipientFilter = {
        isVerified: !!filterVerified,
        isPremium: !!filterPremium
      };
      if (roleFilter) recipientFilter.role = roleFilter;

      const res = await adminSendEmailBlast({
        templateId: selectedTemplateId,
        subject: (blastSubject || '').trim(),
        recipientFilter,
        maxRecipients: Number(maxRecipients) || 0
      });
      const pid = res?.data?.progressId;
      setProgressId(pid || '');
      setMessage({ type: 'success', text: `Envío iniciado. Total: ${res?.data?.totalRecipients ?? '—'}` });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'No se pudo iniciar el envío' });
    } finally {
      setBusy(false);
    }
  };

  const MessageBox = () => {
    if (!message) return null;
    const cls =
      message.type === 'success'
        ? 'border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-100'
        : 'border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-100';
    return <div className={`mt-6 rounded-2xl border p-4 text-sm ${cls}`}>{message.text}</div>;
  };

  return (
    <Layout>
      <div className="py-8">
        <div className="container-custom">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Herramientas de administración (emails y mantenimiento).
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Link to="/admin/migraciones">
                <Button variant="outline">Migraciones</Button>
              </Link>
            </div>
          </div>

          <MessageBox />

          {!isAdmin && (
            <div className="mt-6 p-5 rounded-2xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20">
              <h2 className="text-lg font-semibold text-red-900 dark:text-red-100">Acceso restringido</h2>
              <p className="mt-2 text-sm text-red-800 dark:text-red-200">
                Este panel está habilitado únicamente para administradores.
              </p>
            </div>
          )}

          {isAdmin && (
            <div className="mt-6">
              <nav className="flex flex-nowrap gap-2 mb-6 overflow-x-auto overflow-y-hidden pb-1" aria-label="Secciones del panel" style={{ WebkitOverflowScrolling: 'touch' }}>
                {[
                  { id: 'inicio', label: 'Inicio' },
                  { id: 'emails', label: 'Emails' },
                  { id: 'plantillas', label: 'Plantillas' },
                  { id: 'usuarios', label: 'Usuarios' },
                  { id: 'cuenta', label: 'Cuenta' }
                ].map(({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setActiveTab(id)}
                    className={`shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                      activeTab === id
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
                <Link
                  to="/admin/migraciones"
                  className="shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Migraciones
                </Link>
              </nav>

              {activeTab === 'inicio' && (
                <div className="space-y-6">
                  <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Bienvenido al panel de administración</h2>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      Desde aquí podés gestionar emails, plantillas y herramientas de mantenimiento. Elegí una sección en las pestañas de arriba.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <button
                      type="button"
                      onClick={() => setActiveTab('emails')}
                      className="rounded-2xl border-2 border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 text-left hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900 dark:text-white block">Enviar email de prueba</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400 mt-1 block">Usar plantilla y enviar test o envío masivo.</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('plantillas')}
                      className="rounded-2xl border-2 border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 text-left hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900 dark:text-white block">Gestionar plantillas</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400 mt-1 block">Crear y editar plantillas de email.</span>
                    </button>
                    <Link
                      to="/admin/migraciones"
                      className="rounded-2xl border-2 border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 text-left hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors no-underline"
                    >
                      <span className="font-semibold text-gray-900 dark:text-white block">Migraciones</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400 mt-1 block">Herramientas de migración de datos.</span>
                    </Link>
                    <button
                      type="button"
                      onClick={() => setActiveTab('usuarios')}
                      className="rounded-2xl border-2 border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 text-left hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900 dark:text-white block">Buscar usuarios</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400 mt-1 block">Dar Premium o Embajador por usuario.</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('cuenta')}
                      className="rounded-2xl border-2 border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 text-left hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900 dark:text-white block">Cuenta y herramientas</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400 mt-1 block">Sesión, Bootstrap admin y Premium (debug).</span>
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'cuenta' && (
                <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Sesión y herramientas</h2>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 mb-6">Datos de tu sesión y opciones de mantenimiento para admins.</p>
                  <div className="space-y-4">
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Email</span>
                      <div className="mt-1 text-gray-900 dark:text-white font-medium">{email || '—'}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">UID</span>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 font-mono break-all">{currentUser?.uid || '—'}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Role</span>
                      <div className="mt-1 text-gray-900 dark:text-white">{userData?.role || '—'}</div>
                    </div>
                  </div>
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-slate-700">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Bootstrap admin</h3>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Si recién seteaste el claim admin, podés necesitar cerrar sesión y volver a entrar.
                    </p>
                    <div className="mt-3">
                      <Button variant="outline" loading={busy} onClick={handleBootstrapAdmin}>
                        Refrescar claims
                      </Button>
                    </div>
                  </div>
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-slate-700">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Premium (debug)</h3>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <Button
                        variant="gradient"
                        loading={premiumBusy}
                        onClick={handleGrantPremium}
                        disabled={premiumBusy || userData?.isPremium}
                      >
                        {userData?.isPremium ? 'Premium activo' : 'Darme Premium'}
                      </Button>
                      {userData?.isPremium && (
                        <Button variant="outline" size="sm" loading={premiumBusy} onClick={handleRevokePremium} disabled={premiumBusy}>
                          Quitar Premium
                        </Button>
                      )}
                      {premiumMessage && (
                        <span className={`text-xs ${premiumMessage.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {premiumMessage.text}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'usuarios' && (
                <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Buscar usuarios</h2>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 mb-4">Buscar por nombre, username o email. Dar Premium o Embajador.</p>
                  <div className="mb-4">
                    <input
                      type="text"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      placeholder="Nombre, @usuario o email..."
                      className="w-full max-w-md px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                    />
                  </div>
                  {usersLoading ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Cargando usuarios...</p>
                  ) : (
                    <div className="space-y-3">
                      {filteredUsers.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No hay usuarios que coincidan.</p>
                      ) : (
                        filteredUsers.map((u) => (
                          <div
                            key={u.id}
                            className="flex flex-wrap items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50"
                          >
                            <Avatar
                              src={u.photoURL || u.profileImage}
                              alt={u.displayName || u.username}
                              fallbackText={u.displayName || u.username}
                              size="sm"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-gray-900 dark:text-white truncate">
                                {u.displayName || u.username || '—'}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {u.email || '—'}
                              </div>
                              <div className="text-xs font-mono text-gray-400 dark:text-gray-500 truncate">
                                {u.id}
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {u.isPremium ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  loading={userActionBusy === u.id}
                                  onClick={() => handleRevokePremiumForUser(u.id)}
                                  disabled={userActionBusy != null}
                                >
                                  Quitar Premium
                                </Button>
                              ) : (
                                <Button
                                  variant="gradient"
                                  size="sm"
                                  loading={userActionBusy === u.id}
                                  onClick={() => handleGrantPremiumForUser(u.id)}
                                  disabled={userActionBusy != null}
                                >
                                  Dar Premium
                                </Button>
                              )}
                              {u.isAmbassador ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  loading={userActionBusy === u.id}
                                  onClick={() => handleRevokeAmbassadorForUser(u.id)}
                                  disabled={userActionBusy != null}
                                >
                                  Quitar Embajador
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  loading={userActionBusy === u.id}
                                  onClick={() => handleGrantAmbassadorForUser(u.id)}
                                  disabled={userActionBusy != null}
                                >
                                  Dar Embajador
                                </Button>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'emails' && (
                <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Envío de emails</h2>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 mb-4">Test email y envío masivo con plantilla seleccionada.</p>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Plantilla</label>
                    <select
                      value={selectedTemplateId}
                      onChange={(e) => setSelectedTemplateId(e.target.value)}
                      disabled={templatesLoading || templateBusy}
                      className="w-full max-w-md px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                    >
                      {templates.length === 0 ? <option value="">Sin plantillas</option> : null}
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name || t.id}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-6">
                        <div className="rounded-xl border border-gray-200 dark:border-slate-700 p-4">
                          <h3 className="font-semibold text-gray-900 dark:text-white">Test email</h3>
                          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Para</label>
                              <input
                                value={testTo}
                                onChange={(e) => setTestTo(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                                placeholder="email@dominio.com"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Asunto</label>
                              <input
                                value={testSubject}
                                onChange={(e) => setTestSubject(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                              />
                            </div>
                          </div>
                          <div className="mt-3">
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Mensaje</label>
                            <textarea
                              value={testMessage}
                              onChange={(e) => setTestMessage(e.target.value)}
                              rows={4}
                              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white resize-none"
                            />
                          </div>
                          <div className="mt-3">
                            <Button variant="gradient" loading={busy} onClick={handleSendTest} disabled={!selectedTemplateId}>
                              Enviar test
                            </Button>
                          </div>
                        </div>

                        <div className="rounded-xl border border-gray-200 dark:border-slate-700 p-4">
                          <h3 className="font-semibold text-gray-900 dark:text-white">Envío masivo</h3>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Recomendación: empezar con 3–10 destinatarios.
                          </p>
                          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Asunto</label>
                              <input
                                value={blastSubject}
                                onChange={(e) => setBlastSubject(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Máximo destinatarios</label>
                              <input
                                type="number"
                                min={1}
                                max={200}
                                value={maxRecipients}
                                onChange={(e) => setMaxRecipients(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                              />
                            </div>
                          </div>

                          <div className="mt-3 flex flex-wrap gap-3 items-center">
                            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <input type="checkbox" checked={filterVerified} onChange={(e) => setFilterVerified(e.target.checked)} />
                              Solo verificados
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <input type="checkbox" checked={filterPremium} onChange={(e) => setFilterPremium(e.target.checked)} />
                              Solo premium
                            </label>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Role</span>
                              <input
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value)}
                                placeholder="admin / user / premium"
                                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white text-sm"
                              />
                            </div>
                          </div>

                          <div className="mt-3">
                            <Button variant="primary" loading={busy} onClick={handleBlast} disabled={!selectedTemplateId}>
                              Iniciar envío
                            </Button>
                          </div>

                          {progress && (
                            <div className="mt-4 rounded-xl border border-gray-200 dark:border-slate-700 p-4">
                              <div className="text-sm text-gray-900 dark:text-white font-semibold">
                                Progreso: {progress.progress ?? 0}% ({progress.sentCount ?? 0}/{progress.totalRecipients ?? 0})
                              </div>
                              <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                Estado: {progress.status || '—'} · Errores: {progress.errorCount ?? 0}
                              </div>
                              {Array.isArray(progress.errors) && progress.errors.length > 0 && (
                                <div className="mt-3 text-xs text-red-700 dark:text-red-200 whitespace-pre-wrap">
                                  {progress.errors.slice(-5).join('\n')}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                  </div>
              )}

              {activeTab === 'plantillas' && (
                <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <div className="flex flex-wrap items-center gap-3">
                      <Button variant="gradient" onClick={startCreateTemplate} disabled={templatesLoading}>
                        Nueva plantilla
                      </Button>
                      <select
                        value={selectedTemplateId}
                        onChange={(e) => setSelectedTemplateId(e.target.value)}
                        disabled={templatesLoading || templateBusy}
                        className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white min-w-[200px]"
                      >
                        <option value="">— Seleccionar plantilla —</option>
                        {templates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name || t.id}
                          </option>
                        ))}
                      </select>
                      {selectedTemplate && (
                        <Button variant="outline" onClick={() => startEditTemplate(selectedTemplate)} disabled={!!editing}>
                          Editar
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-4">
                        {!editing ? (
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            Seleccioná una plantilla y presioná “Editar”, o creá una nueva.
                          </div>
                        ) : (
                          <>
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                              <div className="lg:col-span-2 space-y-4">
                            <div className="rounded-xl border border-gray-200 dark:border-slate-700 p-4">
                              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Datos básicos</h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nombre</label>
                                  <input
                                    value={editing.name || ''}
                                    onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Slogan</label>
                                  <input
                                    value={editing.slogan || ''}
                                    onChange={(e) => setEditing((p) => ({ ...p, slogan: e.target.value }))}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="rounded-xl border border-gray-200 dark:border-slate-700 p-4">
                              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Contenido</h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Título</label>
                                <input
                                  value={editing.title || ''}
                                  onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))}
                                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Gradiente header</label>
                                <input
                                  value={editing.headerGradient || ''}
                                  onChange={(e) => setEditing((p) => ({ ...p, headerGradient: e.target.value }))}
                                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                                  placeholder="#8B5CF6 0%, #7C3AED 100%"
                                />
                              </div>
                            </div>
                            <div className="mt-3">
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Mensaje</label>
                              <textarea
                                value={editing.body || ''}
                                onChange={(e) => setEditing((p) => ({ ...p, body: e.target.value }))}
                                rows={5}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white resize-none"
                              />
                            </div>
                            </div>

                            <div className="rounded-xl border border-gray-200 dark:border-slate-700 p-4">
                              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Botón / CTA</h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">CTA texto</label>
                                <input
                                  value={editing.ctaText || ''}
                                  onChange={(e) => setEditing((p) => ({ ...p, ctaText: e.target.value }))}
                                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">CTA URL</label>
                                <input
value={editing.ctaUrl || ''}
                                  onChange={(e) => setEditing((p) => ({ ...p, ctaUrl: e.target.value }))}
                                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                                />
                              </div>
                            </div>
                            </div>

                            <div className="rounded-xl border border-gray-200 dark:border-slate-700 p-4">
                              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Redes sociales</h3>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Twitter</label>
                                <input
                                  value={editing.social?.twitter || ''}
                                  onChange={(e) =>
                                    setEditing((p) => ({ ...p, social: { ...(p.social || {}), twitter: e.target.value } }))
                                  }
                                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                                  placeholder="https://..."
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Instagram</label>
                                <input
                                  value={editing.social?.instagram || ''}
                                  onChange={(e) =>
                                    setEditing((p) => ({ ...p, social: { ...(p.social || {}), instagram: e.target.value } }))
                                  }
                                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                                  placeholder="https://..."
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">GitHub</label>
                                <input
                                  value={editing.social?.github || ''}
                                  onChange={(e) =>
                                    setEditing((p) => ({ ...p, social: { ...(p.social || {}), github: e.target.value } }))
                                  }
                                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                                  placeholder="https://..."
                                />
                              </div>
                            </div>
                            </div>

                            <div className="rounded-xl border border-gray-200 dark:border-slate-700 p-4">
                              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Imágenes</h3>
                              <div className="flex flex-wrap gap-2 mb-3">
                                  <label className="text-xs px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 cursor-pointer">
                                    Subir logo
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={(e) => uploadTemplateImage(e.target.files?.[0], 'logo')}
                                      disabled={templateBusy}
                                    />
                                  </label>
                                  <label className="text-xs px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 cursor-pointer">
                                    Añadir imagen (máx 3)
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={(e) => uploadTemplateImage(e.target.files?.[0], 'image')}
                                      disabled={templateBusy}
                                    />
                                  </label>
                              </div>
                              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                                {editing.logoUrl ? (
                                  <div className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                                    <img src={editing.logoUrl} alt="Logo" className="w-full h-24 object-contain bg-white" />
                                  </div>
                                ) : null}
                                {(editing.images || []).map((img, idx) => (
                                  <div key={idx} className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                                    <img src={img.url} alt="" className="w-full h-24 object-cover" />
                                  </div>
                                ))}
                              </div>
                              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                Se suben a Storage en <code>email-assets/{currentUser?.uid}</code>.
                              </p>
                            </div>

                            <div className="flex gap-2 flex-wrap">
                              <Button variant="gradient" loading={templateBusy} onClick={saveTemplate}>
                                Guardar
                              </Button>
                              <Button variant="secondary" onClick={() => setEditing(null)} disabled={templateBusy}>
                                Cancelar
                              </Button>
                            </div>
                              </div>

                              <div className="lg:col-span-1">
                                <div className="lg:sticky lg:top-6 rounded-xl border border-gray-200 dark:border-slate-700 p-4 bg-gray-50 dark:bg-slate-900/50">
                                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Preview (aproximado)</h3>
                                  <div className="rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                                <div className="p-4 bg-gray-50 dark:bg-slate-900 flex items-center justify-between gap-3">
                                  <div className="flex items-center gap-2 min-w-0">
                                    {editing.logoUrl ? (
                                      <img src={editing.logoUrl} alt="Illustra" className="h-8 w-auto object-contain" />
                                    ) : (
                                      <div className="h-8 w-20 rounded bg-gray-200 dark:bg-slate-700" />
                                    )}
                                    <div className="text-xs text-gray-600 dark:text-gray-400 truncate">{editing.slogan}</div>
                                  </div>
                                </div>
                                <div
                                  className="p-5 text-white"
                                  style={{ background: `linear-gradient(135deg, ${editing.headerGradient || '#8B5CF6 0%, #7C3AED 100%'})` }}
                                >
                                  <div className="text-lg font-bold">{editing.title}</div>
                                </div>
                                <div className="p-5 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200">
                                  <div className="whitespace-pre-wrap text-sm">{editing.body}</div>
                                  {editing.ctaUrl ? (
                                    <div className="mt-4">
                                      <a
                                        href={editing.ctaUrl}
                                        className="inline-block px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold"
                                      >
                                        {editing.ctaText || 'Ver más'}
                                      </a>
                                    </div>
                                  ) : null}
                                  {(editing.images || []).length > 0 ? (
                                    <div className="mt-4 grid grid-cols-2 gap-2">
                                      {editing.images.slice(0, 3).map((img, idx) => (
                                        <img key={idx} src={img.url} alt="" className="w-full h-32 object-cover rounded-lg" />
                                      ))}
                                    </div>
                                  ) : null}
                                  <div className="mt-5 pt-4 border-t border-gray-200 dark:border-slate-700 text-xs text-gray-500 dark:text-gray-400">
                                    {editing.social?.twitter || editing.social?.instagram || editing.social?.github ? (
                                      <div className="flex gap-3 flex-wrap">
                                        {editing.social?.twitter ? <a href={editing.social.twitter}>Twitter</a> : null}
                                        {editing.social?.instagram ? <a href={editing.social.instagram}>Instagram</a> : null}
                                        {editing.social?.github ? <a href={editing.social.github}>GitHub</a> : null}
                                      </div>
                                    ) : (
                                      <div>—</div>
                                    )}
                                  </div>
                                </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                  </div>
                </div>
              )}

        </div>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default AdminDashboardPage;

